import {
  Box,
  Flex,
  HStack,
  IconButton,
  Spacer,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FaBars, FaChevronLeft, FaChevronRight, FaUser } from "react-icons/fa";
import { FiLogOut } from "react-icons/fi";
import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerRoot,
  DrawerTrigger,
} from "../ui/drawer";
import SidebarItems from "./SidebarItems";
import SidebarTables from "./SidebarTables";
import type { UserPublic } from "@/client";
import useAuth from "@/hooks/useAuth";

const Sidebar = () => {
  const queryClient = useQueryClient();
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"]);
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    logout();
  };

  // Determine collapsed state for desktop based on breakpoint.
  // When below md, sidebar is collapsed.
  const collapsedBreakpoint = useBreakpointValue({ base: true, md: false });
  const [isCollapsed, setIsCollapsed] = useState(collapsedBreakpoint ?? false);

  useEffect(() => {
    if (collapsedBreakpoint !== undefined) {
      setIsCollapsed(collapsedBreakpoint);
    }
  }, [collapsedBreakpoint]);

  const toggleSidebar = () => {
    setIsCollapsed((prev) => !prev);
  };

  // Determine if device is mobile (less than sm).
  // By default, Chakra's "sm" is 30em (~480px).
  const isMobile = useBreakpointValue({ base: true, sm: false });

  // For mobile devices, render the Drawer-based sidebar.
  if (isMobile) {
    return (
      <DrawerRoot
        placement="start"
        open={open}
        onOpenChange={(e) => setOpen(e.open)}
      >
        <DrawerBackdrop />
        <DrawerTrigger asChild>
          <IconButton
            variant="ghost"
            color="inherit"
            display={{ base: "flex", sm: "none" }}
            aria-label="Open Menu"
            position="absolute"
            zIndex="100"
            m={4}
          >
            <FaBars />
          </IconButton>
        </DrawerTrigger>
        <DrawerContent maxW="272px">
          <DrawerCloseTrigger />
          <DrawerBody>
            <Flex flexDir="column" justify="space-between" height="100%">
              {/* Top: Sidebar Items */}
              <Box>
                <SidebarItems onClose={() => setOpen(false)} />
              </Box>

              {/* Bottom: Logout and User Info */}
              <Box>
                <Flex
                  as="button"
                  onClick={handleLogout}
                  alignItems="center"
                  gap={4}
                  px={4}
                  py={2}
                >
                  <FiLogOut />
                  <Text>로그아웃</Text>
                </Flex>
                {currentUser?.email && (
                  <Text fontSize="sm" p={2}>
                    사용자: {currentUser.email}
                  </Text>
                )}
              </Box>
            </Flex>
          </DrawerBody>
          <DrawerCloseTrigger />
        </DrawerContent>
      </DrawerRoot>
    );
  }

  // For screens sm and above, render the desktop sidebar.
  return (
    <Flex
      direction="column"
      width={isCollapsed ? "80px" : "272px"}
      p={4}
      gap="10px"
      bg="bg.option1"
      alignItems={isCollapsed ? "center" : "flex-start"}
    >
      <HStack gap={2} justify="center" w="100%">
        {!isCollapsed && (
          <IconButton
            aria-label="User"
            size="sm"
            variant="ghost"
            _hover={{ background: "gray.subtle" }}
          >
            <FaUser />
          </IconButton>
        )}
        <Spacer />
        <IconButton
          aria-label="Toggle sidebar"
          size="sm"
          variant="ghost"
          _hover={{ background: "gray.subtle" }}
          onClick={toggleSidebar}
        >
          {isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
        </IconButton>
      </HStack>

      {/* Sidebar items adjust based on isCollapsed */}
      <SidebarItems isCollapsed={isCollapsed} />

      <SidebarTables isCollapsed={isCollapsed} />

      {/* <Conversations isCollapsed={isCollapsed} /> */}

      <Spacer />

      <Box textAlign="center">
        {isCollapsed ? (
          <IconButton
            aria-label="User"
            size="sm"
            variant="ghost"
            _hover={{ background: "gray.subtle" }}
          >
            <FaUser />
          </IconButton>
        ) : (
          <Box>
            <Flex
              as="button"
              onClick={handleLogout}
              alignItems="center"
              gap={4}
              px={4}
              py={2}
            >
              <FiLogOut />
              <Text>로그아웃</Text>
            </Flex>
            {currentUser?.email && (
              <Text fontSize="sm" p={2}>
                사용자: {currentUser.email}
              </Text>
            )}
          </Box>
        )}
      </Box>
    </Flex>
  );
};

export default Sidebar;
